'use client'

import { useState } from 'react'
import { useAlerts } from '@/hooks/useAlerts'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Settings2, Bell, BellOff, Volume2, VolumeX } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

export function AlertSettingsModal() {
  const { settings, updateSettings, testAlert } = useAlerts()
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Settings2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {settings.soundEnabled ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
            경보 설정
          </DialogTitle>
          <DialogDescription>
            실시간 환자 위험 경보 알림 설정
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="sound" className="text-sm font-medium">
                소리 알림
              </Label>
              <p className="text-xs text-muted-foreground">
                경보 발생 시 소리로 알림
              </p>
            </div>
            <Switch
              id="sound"
              checked={settings.soundEnabled}
              onCheckedChange={(checked) => updateSettings({ soundEnabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="nightShift" className="text-sm font-medium">
                야간 근무 시간만
              </Label>
              <p className="text-xs text-muted-foreground">
                21:00 - 06:00 시간대만
              </p>
            </div>
            <Switch
              id="nightShift"
              checked={settings.nightShiftOnly}
              onCheckedChange={(checked) => updateSettings({ nightShiftOnly: checked })}
              disabled={!settings.soundEnabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="snooze" className="text-sm font-medium">
              다시 알림 시간
            </Label>
            <Select
              value={settings.snoozeDuration.toString()}
              onValueChange={(value) => updateSettings({ snoozeDuration: parseInt(value) })}
            >
              <SelectTrigger id="snooze">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5분</SelectItem>
                <SelectItem value="10">10분</SelectItem>
                <SelectItem value="15">15분</SelectItem>
                <SelectItem value="30">30분</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border p-3 bg-muted/50">
            <div className="flex items-center gap-2 text-sm font-medium mb-1">
              {settings.soundEnabled ? (
                <Volume2 className="h-4 w-4 text-green-600" />
              ) : (
                <VolumeX className="h-4 w-4 text-muted-foreground" />
              )}
              현재 상태
            </div>
            <p className="text-xs text-muted-foreground">
              소리: {settings.soundEnabled ? '켜짐' : '꺼짐'}
              {settings.soundEnabled && settings.nightShiftOnly && ' (야간만)'}
              {' • '}다시 알림: {settings.snoozeDuration}분
            </p>
          </div>

          <Button
            onClick={() => {
              console.log('Test alert button clicked!')
              // First, test direct sound play
              const audio = new Audio('/sounds/alert.mp3')
              audio.volume = 0.6
              audio.play()
                .then(() => console.log('Test sound played successfully!'))
                .catch(err => {
                  console.error('Test sound failed:', err)
                  if (err.name === 'NotAllowedError') {
                    alert('브라우저에서 소리 재생을 차단했습니다. 페이지와 상호작용 후 다시 시도해주세요.')
                  }
                })
              // Also call the regular test alert
              testAlert()
            }}
            variant="outline"
            className="w-full"
          >
            테스트 경보
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}